import {
  CompleteKundali,
  BirthDetails,
  PlanetName,
  ZodiacSign,
  PlanetPosition,
  DashaPeriod
} from '../types/kundali';
import {
  calculateKundali,
  calculateAscendant,
  calculateDoubleTransit,
  calculateVimshottariDasha,
  getActiveAyanamsha,
  getLahiriAyanamsha,
  ZODIAC_SIGNS,
  ZODIAC_LORDS,
  NAKSHATRAS,
  DASHA_ORDER
} from './vedicEngine';

export const BTR_ENGINE_VERSION = 'BTR-FP-V6.4-CANONICAL';
export const BTR_RULESET_VERSION = 'VEDANG-BTR-V6.4-CANONICAL-CORE';

// ============================================================================
// SECTION 6, 7 & 10: EVENT QUALITY HIERARCHY, RELIABILITY & LEDGER
// ============================================================================

export type EventTier = 'TIER_A' | 'TIER_B' | 'TIER_C';

export type EventCategory =
  | 'MARRIAGE'
  | 'CHILDREN'
  | 'CAREER'
  | 'BUSINESS'
  | 'RELOCATION'
  | 'PROPERTY'
  | 'HEALTH'
  | 'FAMILY'
  | 'EDUCATION'
  | 'FINANCE'
  | 'LEGAL'
  | 'SPIRITUAL'
  | 'OTHER';

export type DatePrecision =
  | 'Exact'       // Day or better
  | 'High'        // Within days
  | 'Moderate'    // Within weeks
  | 'Low'         // Months / broad periods
  | 'Month/Year'  // Month and Year
  | 'Year Only'   // Year only
  | 'Approximate' // Approximate
  | 'Unknown';

export type SourceReliability =
  | 'Official document'
  | 'Hospital record'
  | 'Marriage certificate'
  | 'Employment record'
  | 'Educational record'
  | 'Property record'
  | 'Contemporaneous diary'
  | 'Family memory'
  | 'Personal recollection'
  | 'Inferred date';

// Section 3: Birth Time Source
export type BirthTimeSource =
  | 'Exact official record'
  | 'Hospital record'
  | 'Certificate'
  | 'Contemporaneous family record'
  | 'Family recollection'
  | 'Approximate memory'
  | 'Rounded time'
  | 'Inferred time'
  | 'Completely unknown';

// TimeSource alias for backwards compatibility
export type TimeSource =
  | 'Birth certificate'
  | 'Hospital record'
  | 'Official document'
  | 'Parent memory'
  | 'Family memory'
  | 'Personal memory'
  | "Previous astrologer's rectification"
  | 'Approximate estimate'
  | 'Unknown'
  | BirthTimeSource;

export interface RectificationEvent {
  id: string;
  category: EventCategory;
  eventType?: string;
  tier?: EventTier;
  description: string;
  date: string; // YYYY-MM-DD
  datePrecision: DatePrecision;
  sourceReliability?: SourceReliability;
  independenceGroup?: string; // Section 10 & 11: Correlated event clustering
  location?: string;
  ageAtEvent?: number;
  importance?: 'Very High' | 'High' | 'Medium' | 'Low' | 'Minor';
  reliability?: 'High' | 'Medium' | 'Low';
  clusterId?: string;
  userProvided?: boolean;
  astrologicalHouses: number[];
  planets: PlanetName[];
  vargas: string[]; // e.g. D9, D10, D7, D4, D12, D6, D24, D20
}

export interface EventCluster {
  clusterId: string;
  clusterName: string;
  eventIds: string[];
  primaryEventId: string;
  correlationPenaltyFactor: number;
}

// ============================================================================
// SECTION 4: FOUR TIME STATES & SEARCH ENVELOPE MODEL
// ============================================================================

export interface FourTimeStates {
  recordedTime: string;      // Immutable historical evidence (HH:mm:ss)
  workingTime: string;       // Active analytical selection (HH:mm:ss)
  candidateInterval: {
    startDateTime: string;
    endDateTime: string;
    intervalString: string;
    justifiedPrecision: string;
    isPlateau: boolean;
  };
  confirmedTime: string | null; // Only exists after explicit user confirmation
  confirmationStatus: 'UNCONFIRMED' | 'CONFIRMED';
}

export interface SearchEnvelope {
  startDateTime: string;
  endDateTime: string;
  windowMinutes: number;
  source: string;
  rationale: string;
}

export interface SupportedSearchEnvelope {
  label: string;
  minutes: number;
  description: string;
}

export const SUPPORTED_SEARCH_ENVELOPES: SupportedSearchEnvelope[] = [
  { label: '±15m', minutes: 15, description: '±15 minutes (Hospital record / Exact certificate)' },
  { label: '±30m', minutes: 30, description: '±30 minutes (Contemporaneous family record)' },
  { label: '±1h', minutes: 60, description: '±1 hour (Rounded / Family memory)' },
  { label: '±2h', minutes: 120, description: '±2 hours (Approximate memory / Shift boundary)' },
  { label: '±6h', minutes: 360, description: '±6 hours (Morning / Evening / Quarter day)' },
  { label: '±12h', minutes: 720, description: '±12 hours (Day / Night uncertainty)' },
  { label: '±24h', minutes: 1440, description: '±24 hours (Date boundary / ±1 day sweep)' },
  { label: '±7d', minutes: 10080, description: '±7 days (Week / Lunar phase window)' },
  { label: '±30d', minutes: 43200, description: '±30 days (Month / Season window)' },
  { label: '±1y', minutes: 525600, description: '±1 year (Annual window)' },
  { label: '±2y', minutes: 1051200, description: '±2 years (Multi-year macro sweep)' }
];

// ============================================================================
// SECTION 3 & 4: INPUT INTEGRITY AUDIT & SEPARATION OF UNCERTAINTIES
// ============================================================================

export interface InputIntegrityAudit {
  isDateCertain: boolean;
  calendarConvention: 'Gregorian' | 'Julian' | 'Hindu Luni-Solar';
  midnightBoundaryChecked: boolean;
  locationIntegrity: {
    placeName: string;
    coordinatesValid: boolean;
    latitude: number;
    longitude: number;
  };
  civilTimeIntegrity: {
    timezoneOffsetMinutes: number;
    timezoneName?: string;
    dstChecked: boolean;
    warning?: string;
  };
  timeSource: BirthTimeSource;
  documentaryUncertaintyMinutes: number;
  documentaryUncertaintyEnvelope: string;
  searchWindowMinutes: number;
  searchWindowEnvelope: string;
  auditVerdict: 'PASSED' | 'CAUTION' | 'ACTION_REQUIRED';
  auditNotes: string[];
}

export function performInputIntegrityAudit(
  reportedTime: string,
  birthDate: string,
  placeName: string,
  latitude: number,
  longitude: number,
  timezoneOffsetMinutes: number,
  timeSource: BirthTimeSource = 'Family recollection',
  userWindowMinutes?: number
): InputIntegrityAudit {
  const notes: string[] = [];
  let verdict: 'PASSED' | 'CAUTION' | 'ACTION_REQUIRED' = 'PASSED';

  // 1. Date check
  const isDateCertain = Boolean(birthDate && birthDate.length >= 10);
  if (!isDateCertain) {
    notes.push('Birth date lacks day-level specificity. Date certainty cannot be confirmed.');
    verdict = 'ACTION_REQUIRED';
  }

  // 2. Midnight / boundary warning
  const [h = 12, m = 0] = reportedTime.split(':').map(Number);
  const midnightBoundaryChecked = (h === 0 || (h === 23 && m >= 45) || (h === 0 && m <= 15));
  if (midnightBoundaryChecked) {
    notes.push('Birth occurred within ±15 minutes of midnight. Date boundary rollover active.');
  }

  // 3. Location integrity
  const coordinatesValid = !isNaN(latitude) && !isNaN(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
  if (!coordinatesValid) {
    notes.push('Geographic coordinates are invalid. Astrological ascendant cannot be reliably computed.');
    verdict = 'ACTION_REQUIRED';
  }

  // 4. Civil time & timezone integrity
  const dstChecked = true;
  let warning: string | undefined;
  if (Math.abs(timezoneOffsetMinutes) > 840) {
    warning = 'Timezone offset exceeds standard UTC ±14h limits.';
    verdict = 'CAUTION';
  }

  // 5. Separate Documentary Uncertainty from Forensic Search Window (Section 7)
  let documentaryMinutes = 15;
  switch (timeSource) {
    case 'Exact official record':
    case 'Hospital record':
    case 'Certificate':
      documentaryMinutes = 15;
      break;
    case 'Contemporaneous family record':
      documentaryMinutes = 30;
      break;
    case 'Rounded time':
    case 'Family recollection':
      documentaryMinutes = 60;
      break;
    case 'Approximate memory':
      documentaryMinutes = 120;
      break;
    case 'Inferred time':
    case 'Completely unknown':
      documentaryMinutes = 360;
      break;
  }

  const searchWindowMinutes = userWindowMinutes !== undefined ? userWindowMinutes : documentaryMinutes;

  const formatEnvelope = (mins: number) => {
    if (mins >= 1051200) return `±${(mins / 525600).toFixed(1)} years`;
    if (mins >= 525600) return `±1 year`;
    if (mins >= 43200) return `±${(mins / 43200).toFixed(1)} months`;
    if (mins >= 1440) return `±${(mins / 1440).toFixed(1)} days`;
    if (mins >= 60) return `±${(mins / 60).toFixed(1)} hours`;
    return `±${mins} minutes`;
  };

  const documentaryUncertaintyEnvelope = `${formatEnvelope(documentaryMinutes)} (${timeSource})`;
  const searchWindowEnvelope = formatEnvelope(searchWindowMinutes);

  return {
    isDateCertain,
    calendarConvention: 'Gregorian',
    midnightBoundaryChecked,
    locationIntegrity: {
      placeName,
      coordinatesValid,
      latitude,
      longitude
    },
    civilTimeIntegrity: {
      timezoneOffsetMinutes,
      dstChecked,
      warning
    },
    timeSource,
    documentaryUncertaintyMinutes: documentaryMinutes,
    documentaryUncertaintyEnvelope,
    searchWindowMinutes,
    searchWindowEnvelope,
    auditVerdict: verdict,
    auditNotes: notes
  };
}

// ============================================================================
// SECTION 16, 17, 18: CONTRADICTION & SUPPORT EVIDENCE
// ============================================================================

export interface ContradictionItem {
  event: string;
  method: 'Parashari' | 'KP' | 'Varga Corroboration' | 'Transits' | 'Jaimini' | 'Ruling Planets';
  nature: string;
  severity: 'High' | 'Medium' | 'Low';
  penalty: number;
}

export interface SupportingEvidenceItem {
  event: string;
  method: 'Parashari' | 'KP' | 'Varga Corroboration' | 'Transits' | 'Jaimini' | 'Ruling Planets';
  explanation: string;
  strength: 'Strong' | 'Moderate';
}

export interface MethodologyAgreementItem {
  methodology: string;
  score: number;
  status: 'Strongly Supports' | 'Supports' | 'Partial' | 'Contradicts' | 'Neutral';
  details: string;
}

export interface CandidateScores {
  eventFit: number;
  dashaFit: number;
  transitFit: number;
  vargaFit: number;
  nakshatraFit: number;
  jaiminiFit: number;
  ashtakavargaFit: number;
  lagnaFit: number;
  kpFit: number;
  boundaryStability: number;
  crossModelAgreement: number;
  contradictionPenalty: number;
  correlatedPenalty: number;
}

export interface CandidateTime {
  candidateDateTime: string; // ISO string e.g. "1992-04-23T12:04:15.000Z"
  date: string;              // "1992-04-23"
  timeString: string;        // "12:04:15"
  offsetMinutes: number;     // Relative to recorded base time
  offsetSeconds: number;
  scores: CandidateScores;
  evidenceRankingScore: number; // Section 19: Evidence Ranking Score (0-100)
  totalScore: number;           // alias for backwards compatibility
  vargaLagnas: { [varga: string]: string };
  kpSubLords?: { [cusp: number]: string };
  startingDashaBalance: string;
  activeMoonNakshatra?: string;
  activeLagnaDegree?: number;
  supportingEvidence: SupportingEvidenceItem[];
  contradictions: ContradictionItem[];
  kundali?: CompleteKundali;
}

export interface AlternativeCandidate {
  timeString: string;
  evidenceScore: number;
  interval: string;
  rejectionReason: string;
  margin: number;
}

// ============================================================================
// SECTION 21 & 22: ANTI-OVERFITTING VALIDATION
// ============================================================================

export interface LOOResult {
  removedEventId: string;
  removedEventDescription: string;
  originalBestTime: string;
  newBestTime: string;
  fitPreserved: boolean;
  scoreDifference: number;
}

export interface HoldOutResult {
  trainingEventsCount: number;
  validationEventsCount: number;
  bestTrainingTime: string;
  trainingScore: number;
  validationScoreOnTrainingTime: number;
  predictionSuccess: boolean;
}

// ============================================================================
// SECTION 31 & 32: REQUIRED USER-FACING FORENSIC RESULT
// ============================================================================

export interface CascadeStageSummary {
  stage: 'Macro/Sign' | 'Meso/Minutes' | 'Micro/Seconds' | 'Boundary/Fine';
  searchSpan: string;
  stepSize: string;
  evaluatedCandidates: number;
  bestTime: string;
  bestScore: number;
}

export interface ForensicBtrResult {
  timestamp: string; // Rectified birth time (only if justified)
  offsetSeconds: number;
  confidence: 'VERY HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE';
  confidenceScore: number;
  precisionJustified: string; // e.g. "±3.5 minutes (Interval: 14:18 - 14:25)"
  mostSupportedInterval: string;
  recordedTime: string;
  workingTime: string;
  confirmedTime: string | null;
  confirmationStatus: 'UNCONFIRMED' | 'CONFIRMED';
  bestCandidate?: CandidateTime;
  strongestSupportingEvidence: SupportingEvidenceItem[];
  strongestContradictoryEvidence: ContradictionItem[];
  methodologyAgreement: MethodologyAgreementItem[];
  alternativeCandidates: AlternativeCandidate[];
  validationResult: {
    looStability: string;
    holdOutAccuracy?: string;
    overfittingRisk: 'Low' | 'Moderate' | 'High';
    summary: string;
  };
  remainingUncertainty: string;
  finalConclusion: string; // Adhering to Section 33 Language Safety
  inputAudit: InputIntegrityAudit;
  cascadeStages: CascadeStageSummary[];
  searchEnvelope: SearchEnvelope;
  auditTrail: {
    engineVersion: string;
    ruleset: string;
    evaluatedCandidatesCount: number;
    eventsProcessed: number;
    independentEventCount: number;
    timestamp: string;
  };
}

export type BtrEngineResult = ForensicBtrResult;

export interface BtrEngineRunParams {
  baseTime: string;
  birthDate?: string;
  placeName?: string;
  coordinates?: { latitude: number; longitude: number };
  timezoneOffsetMinutes?: number;
  timeSource?: BirthTimeSource;
  responses?: Record<string, any>;
  events?: RectificationEvent[];
  searchWindowMinutes?: number;
  workingTime?: string;
  confirmedTime?: string | null;
  confirmationStatus?: 'UNCONFIRMED' | 'CONFIRMED';
}

// ============================================================================
// CLASSICAL JYOTISHA RULES DATABASE (Section 11 & 33)
// ============================================================================

export const TRADITIONS = [
  {
    name: "Parashari Event Alignment",
    rule: "Mahadasha/Antardasha lord active on historical event date connects with event's karaka and house lord in D1 and corresponding Varga.",
    source: "Brihat Parashara Hora Shastra, Ch. 46-50",
    confidence: "Very High"
  },
  {
    name: "Double Transit Rule (Guru-Shani Gochar)",
    rule: "Saturn and Jupiter simultaneously aspect or occupy the event's primary house or trines on the historical milestone date.",
    source: "Devakeralam (Chandrakala Nadi), Vol. 2",
    confidence: "Very High"
  },
  {
    name: "KP Cusp Sub-Lord Rule",
    rule: "The sub-lord of the event cusp signifies the favorable event fruition houses and is not situated in detrimental 6th/8th/12th negations.",
    source: "Krishnamurti Padhdhati (KP Reader III)",
    confidence: "High"
  },
  {
    name: "Varga Corroboration",
    rule: "The Lagna lord of relevant divisional charts (D9 for Marriage, D10 for Career, D7 for Children, D4 for Property) maintains dignity.",
    source: "Sarvartha Chintamani, Ch. 2",
    confidence: "High"
  },
  {
    name: "D60 Shashtiamsha Restriction",
    rule: "D60 deity alignment is micro-corroborative only; it must never independently determine rectified time or fabricate false second precision.",
    source: "Brihat Parashara Hora Shastra, Ch. 6; Forensic Mandate",
    confidence: "High"
  },
  {
    name: "Jaimini Chara Dasha Alignment",
    rule: "Major career achievements correlate with Chara Dashas of signs holding Amatyakaraka (AmK) or Arudha Lagna.",
    source: "Jaimini Upadesha Sutras, Ch. 2",
    confidence: "High"
  }
];

// ============================================================================
// COMPREHENSIVE FACTUAL EVENT QUESTIONS (Section 6: Tier A, B, C)
// ============================================================================

export interface StructuralQuestion {
  id: string;
  tier: EventTier;
  category: EventCategory;
  questionText: string;
  questionTextHi: string;
  houses: number[];
  planets: PlanetName[];
  vargas: string[];
  rationale: string;
}

export const STRUCTURAL_QUESTIONS: StructuralQuestion[] = [
  // TIER A — High-Value Events
  {
    id: 'marriage_q',
    tier: 'TIER_A',
    category: 'MARRIAGE',
    questionText: "What was the exact date of your marriage or civil union ceremony?",
    questionTextHi: "आपके विवाह या औपचारिक परिणय संस्कार की सटीक तिथि क्या थी?",
    houses: [7, 2, 11],
    planets: ['Venus', 'Jupiter'],
    vargas: ['D9'],
    rationale: "Marriage triggers the 7th bhava, 2nd (kutumba), and 11th (desire fulfillment). Corroborated in Navamsha (D9)."
  },
  {
    id: 'first_child_q',
    tier: 'TIER_A',
    category: 'CHILDREN',
    questionText: "What was the date of birth of your first child?",
    questionTextHi: "आपकी प्रथम संतान के जन्म की सटीक तिथि क्या थी?",
    houses: [5, 9, 11],
    planets: ['Jupiter'],
    vargas: ['D7'],
    rationale: "Childbirth strongly anchors the 5th house, Jupiter, and Saptamsha (D7) Lagna."
  },
  {
    id: 'career_first_q',
    tier: 'TIER_A',
    category: 'CAREER',
    questionText: "When did you secure your first formal employment or primary career breakthrough?",
    questionTextHi: "आपको अपनी पहली औपचारिक नौकरी या करियर की मुख्य सफलता किस तिथि को मिली?",
    houses: [10, 1, 6, 11],
    planets: ['Sun', 'Saturn', 'Mercury'],
    vargas: ['D10'],
    rationale: "First employment activates the 10th bhava (karmasthana), 6th (service), and Dashamsha (D10)."
  },
  {
    id: 'parent_demise_q',
    tier: 'TIER_A',
    category: 'FAMILY',
    questionText: "If applicable, what was the date of passing of your father or mother?",
    questionTextHi: "यदि लागू हो, तो आपके पिता अथवा माता के देहावसान की तिथि क्या थी?",
    houses: [9, 4, 8, 12],
    planets: ['Sun', 'Moon', 'Saturn'],
    vargas: ['D12'],
    rationale: "Demise of a parent is an immovable anchor triggering 9th/4th house maraka/badhaka in D1 & D12."
  },
  {
    id: 'major_surgery_q',
    tier: 'TIER_A',
    category: 'HEALTH',
    questionText: "What was the date of any major surgery, severe accident, or emergency hospitalization?",
    questionTextHi: "क्या आपका कोई बड़ा ऑपरेशन, गंभीर दुर्घटना अथवा आपातकालीन अस्पताल भर्ती हुई? उसकी तिथि क्या थी?",
    houses: [1, 6, 8, 12],
    planets: ['Mars', 'Saturn', 'Rahu'],
    vargas: ['D6', 'D9'],
    rationale: "Accidents/surgeries activate 6th/8th dusthanas, Mars (karaka of surgery/cuts), and D6 Shashtamsha."
  },
  {
    id: 'foreign_relocation_q',
    tier: 'TIER_A',
    category: 'RELOCATION',
    questionText: "When did you undertake a major relocation or permanent settlement in a foreign country?",
    questionTextHi: "आपने किसी दूसरे शहर या विदेश में स्थायी निवास/स्थानांतरण किस तिथि को किया?",
    houses: [4, 9, 12, 3],
    planets: ['Moon', 'Rahu', 'Jupiter'],
    vargas: ['D4', 'D9'],
    rationale: "Foreign relocation involves leaving home (4th afflicted/negated) and settling overseas (9th & 12th houses)."
  },
  {
    id: 'property_purchase_q',
    tier: 'TIER_A',
    category: 'PROPERTY',
    questionText: "What was the date of signing the deed for your first residential home or major real estate purchase?",
    questionTextHi: "आपने अपने प्रथम अचल संपत्ति (मकान, भूमि, फ्लैट) की रजिस्ट्री किस तिथि को कराई?",
    houses: [4, 11],
    planets: ['Mars', 'Saturn'],
    vargas: ['D4'],
    rationale: "Real estate and fixed assets anchor the 4th house and Chaturthamsha (D4)."
  },
  {
    id: 'business_launch_q',
    tier: 'TIER_A',
    category: 'BUSINESS',
    questionText: "What was the launch or formal incorporation date of your independent business or venture?",
    questionTextHi: "आपने अपने स्वतंत्र व्यवसाय, स्टार्टअप अथवा साझेदारी की औपचारिक शुरुआत किस तिथि को की?",
    houses: [7, 10, 11, 2],
    planets: ['Mercury', 'Jupiter', 'Sun'],
    vargas: ['D10', 'D2'],
    rationale: "Independent enterprise activates the 7th house (commerce) and 10th house (profession) in D1 and D10."
  },
  {
    id: 'litigation_divorce_q',
    tier: 'TIER_A',
    category: 'LEGAL',
    questionText: "What was the date of any major legal verdict, divorce decree, or significant court resolution?",
    questionTextHi: "क्या कोई बड़ा अदालती फैसला, वैवाहिक विच्छेद (तलाक), या कानूनी जीत/हार हुई? उसकी तिथि क्या थी?",
    houses: [6, 8, 12],
    planets: ['Saturn', 'Mars', 'Rahu'],
    vargas: ['D9', 'D6'],
    rationale: "Court disputes and dissolution activate the 6th bhava (litigation) and 8th/12th houses in D1 & D9."
  },

  // TIER B — Supporting Events
  {
    id: 'graduation_q',
    tier: 'TIER_B',
    category: 'EDUCATION',
    questionText: "What was the month and year of receiving your university degree or highest academic honor?",
    questionTextHi: "आपने अपनी उच्च शिक्षा (स्नातक/परास्नातक डिग्री) किस माह और वर्ष में पूरी की?",
    houses: [4, 5, 9],
    planets: ['Mercury', 'Jupiter'],
    vargas: ['D24'],
    rationale: "Higher education activates the 5th (intellect) and 9th (higher learning) houses in D1 and D24."
  },
  {
    id: 'vehicle_purchase_q',
    tier: 'TIER_B',
    category: 'PROPERTY',
    questionText: "When did you acquire your first personal motor vehicle or car?",
    questionTextHi: "आपने अपना पहला निजी वाहन (कार अथवा दुपहिया) किस माह/वर्ष में खरीदा?",
    houses: [4, 11],
    planets: ['Venus'],
    vargas: ['D4', 'D16'],
    rationale: "Vehicles (Vahana sukha) activate the 4th house, Venus, and Shodashamsha (D16)."
  },
  {
    id: 'major_promotion_q',
    tier: 'TIER_B',
    category: 'CAREER',
    questionText: "What was the date of your most significant career promotion or executive elevation?",
    questionTextHi: "आपको अपने करियर में सबसे बड़ा पदोन्नति (प्रमोशन) या सम्मान किस तिथि को प्राप्त हुआ?",
    houses: [10, 11, 1],
    planets: ['Sun', 'Jupiter'],
    vargas: ['D10'],
    rationale: "Promotion triggers elevation of status (10th house) and gains (11th house) in D10."
  },
  {
    id: 'financial_gain_q',
    tier: 'TIER_B',
    category: 'FINANCE',
    questionText: "When did you experience a major financial windfall, inheritance, or high-value equity exit?",
    questionTextHi: "आपको अचानक कोई बड़ा आर्थिक लाभ, पैतृक संपत्ति, या शेयर/इक्विटी का बड़ा लाभ कब मिला?",
    houses: [2, 11, 8],
    planets: ['Jupiter', 'Mercury'],
    vargas: ['D2', 'D11'],
    rationale: "Windfalls activate the 2nd (wealth), 11th (gains), and 8th (unearned wealth) in D1 and D2."
  },
  {
    id: 'sibling_event_q',
    tier: 'TIER_B',
    category: 'FAMILY',
    questionText: "What was the date of birth or major turning point of your sibling?",
    questionTextHi: "आपके छोटे या बड़े भाई/बहन के जन्म अथवा उनके जीवन के मुख्य मोड़ की तिथि क्या थी?",
    houses: [3, 11],
    planets: ['Mars', 'Jupiter'],
    vargas: ['D3'],
    rationale: "Siblings anchor the 3rd (younger) and 11th (elder) houses in D1 and Drekkana (D3)."
  },

  // TIER C — Low-Reliability / Subjective Events (Must NOT independently determine birth time)
  {
    id: 'spiritual_initiation_q',
    tier: 'TIER_C',
    category: 'SPIRITUAL',
    questionText: "Did you undertake a spiritual initiation (Diksha), major pilgrimage, or profound meditation shift?",
    questionTextHi: "क्या आपने कोई आध्यात्मिक दीक्षा, गुरु कृपा, या गहन साधना यात्रा शुरू की? उसका वर्ष क्या था?",
    houses: [9, 5, 12, 8],
    planets: ['Jupiter', 'Ketu'],
    vargas: ['D20'],
    rationale: "Spiritual events are subjective supporting markers that corroborate 9th/12th bhavas and D20."
  },
  {
    id: 'emotional_phase_q',
    tier: 'TIER_C',
    category: 'OTHER',
    questionText: "Was there a broad 1-2 year phase where life felt intensely turbulent or introspective?",
    questionTextHi: "क्या आपके जीवन में कोई ऐसा कालखंड रहा जब मानसिक या भावनात्मक उथल-पुथल अत्यधिक रही?",
    houses: [8, 12, 6],
    planets: ['Saturn', 'Moon', 'Rahu'],
    vargas: ['D1'],
    rationale: "Subjective emotional phases provide weak corroboration only; cannot determine precise time."
  }
];

// ============================================================================
// SECTION 9 & 11: EVENT CLUSTERING & INDEPENDENCE GROUPS
// ============================================================================

export function detectEventClusters(events: RectificationEvent[]): {
  clusters: EventCluster[];
  correlatedPenalty: number;
  independentEventCount: number;
} {
  const clusters: EventCluster[] = [];
  const processed = new Set<string>();

  for (let i = 0; i < events.length; i++) {
    const e1 = events[i];
    if (processed.has(e1.id)) continue;

    const relatedIds = [e1.id];
    const t1 = new Date(e1.date).getTime();

    for (let j = i + 1; j < events.length; j++) {
      const e2 = events[j];
      if (processed.has(e2.id)) continue;

      const t2 = new Date(e2.date).getTime();
      const diffDays = Math.abs(t1 - t2) / (1000 * 60 * 60 * 24);

      // Section 11 & RA-05: Strict independenceGroup precedence over 90-day heuristic:
      // 1. If both events carry explicit independenceGroup:
      //    - If identical => cluster them together into one underlying transition.
      //    - If distinct => NEVER cluster them, respecting user's explicit independence.
      // 2. Only if independenceGroup is absent on either event, fallback to 90-day domain proximity.
      let shouldCluster = false;
      if (e1.independenceGroup && e2.independenceGroup) {
        shouldCluster = e1.independenceGroup === e2.independenceGroup;
      } else {
        const domainMatch =
          (e1.category === 'CAREER' && (e2.category === 'RELOCATION' || e2.category === 'FINANCE')) ||
          (e1.category === 'MARRIAGE' && (e2.category === 'RELOCATION' || e2.category === 'PROPERTY')) ||
          (e1.category === e2.category);
        shouldCluster = diffDays <= 90 && domainMatch;
      }

      if (shouldCluster) {
        relatedIds.push(e2.id);
        processed.add(e2.id);
      }
    }

    if (relatedIds.length > 1) {
      clusters.push({
        clusterId: e1.independenceGroup || `cluster-${i}`,
        clusterName: `${e1.category} Transition Cluster (${e1.date.slice(0, 7)})`,
        eventIds: relatedIds,
        primaryEventId: e1.id,
        correlationPenaltyFactor: 0.25 * (relatedIds.length - 1)
      });
    }
  }

  const correlatedPenalty = clusters.reduce((sum, c) => sum + c.correlationPenaltyFactor * 10, 0);
  const totalClusteredEvents = clusters.reduce((sum, c) => sum + c.eventIds.length, 0);
  const independentEventCount = Math.max(1, (events.length - totalClusteredEvents) + clusters.length);

  return {
    clusters,
    correlatedPenalty: Math.min(25, Math.round(correlatedPenalty)),
    independentEventCount
  };
}

// ============================================================================
// CANONICAL TIME FORMATTING & PARSING HELPERS
// ============================================================================

export function parseHmsToMinutes(timeStr: string): number {
  const [h = 12, m = 0, s = 0] = (timeStr || '12:00:00').split(':').map(Number);
  return h * 60 + m + s / 60;
}

export function formatMinutesToHms(m: number): string {
  const normM = ((m % 1440) + 1440) % 1440;
  const hrs = Math.floor(normM / 60);
  const mins = Math.floor(normM % 60);
  const secs = Math.round((normM % 1) * 60);
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

// ============================================================================
// CLASSICAL KP CUSP SUB-LORD COMPUTATION (Krishnamurti Padhdhati Reader III)
// ============================================================================

export function calculateKpSubLord(deg: number): PlanetName {
  const normDeg = ((deg % 360) + 360) % 360;
  const nakIdx = Math.floor(normDeg / (360 / 27)); // 0..26
  const nakLordIdx = nakIdx % 9;
  const degInNak = normDeg % (360 / 27); // 0 .. 13.33333333 deg
  const minInNak = degInNak * 60; // 0 .. 800 arcminutes

  let accumulatedMin = 0;
  for (let i = 0; i < 9; i++) {
    const planetIdx = (nakLordIdx + i) % 9;
    const planetName = DASHA_ORDER[planetIdx].planet;
    const planetYears = DASHA_ORDER[planetIdx].years;
    const subWidthMin = (planetYears / 120) * 800;
    accumulatedMin += subWidthMin;
    if (minInNak <= accumulatedMin) {
      return planetName;
    }
  }
  return DASHA_ORDER[nakLordIdx].planet;
}

// ============================================================================
// CANONICAL CANDIDATE CONSTRUCTION (CONSUMER OF VEDIC ENGINE)
// ============================================================================

export function createCandidateFromTimestamp(
  targetDateUtc: Date,
  baseDateUtc: Date,
  coordinates: { latitude: number; longitude: number },
  timezoneOffsetMinutes: number,
  baseTimeStr: string
): CandidateTime {
  const localTargetMs = targetDateUtc.getTime() + timezoneOffsetMinutes * 60 * 1000;
  const localTargetDate = new Date(localTargetMs);
  const targetDateStr = localTargetDate.toISOString().split('T')[0];
  const targetTimeStr = localTargetDate.toISOString().split('T')[1].slice(0, 8);

  const offsetMinutes = Math.round(((targetDateUtc.getTime() - baseDateUtc.getTime()) / (60 * 1000)) * 100) / 100;
  const offsetSeconds = Math.round((targetDateUtc.getTime() - baseDateUtc.getTime()) / 1000);

  // Invoke Canonical VedangJyotisham Chart Core
  const candidateBirthDetails: BirthDetails = {
    name: 'BTR Candidate',
    gender: 'other',
    date: targetDateStr,
    time: targetTimeStr,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    placeName: 'BTR Station',
    timezoneOffsetMinutes: timezoneOffsetMinutes
  };

  const candidateKundali = calculateKundali(candidateBirthDetails);
  const ascPlanet = candidateKundali.planets.find(p => p.name === 'Ascendant');
  const ascDegree = ascPlanet ? ascPlanet.longitude : 0;
  const ascSign = (ascPlanet ? ascPlanet.sign : 'Aries') as ZodiacSign;

  // Extract divisional chart lagnas from canonical charts
  const vargaLagnas: { [k: string]: string } = {};
  if (candidateKundali.charts) {
    Object.entries(candidateKundali.charts).forEach(([key, chart]) => {
      if (chart && typeof (chart as any).ascendantIndex === 'number') {
        vargaLagnas[key] = ZODIAC_SIGNS[(chart as any).ascendantIndex] || 'Aries';
      } else if (chart && (chart as any).ascendantSign) {
        vargaLagnas[key] = (chart as any).ascendantSign;
      }
    });
  }
  vargaLagnas['D1'] = ascSign;

  // Real KP Sub-lords from canonical Ascendant
  const kpSubLords: { [k: number]: string } = {
    1: calculateKpSubLord(ascDegree),
    4: calculateKpSubLord((ascDegree + 90) % 360),
    5: calculateKpSubLord((ascDegree + 120) % 360),
    7: calculateKpSubLord((ascDegree + 180) % 360),
    10: calculateKpSubLord((ascDegree + 270) % 360)
  };

  // Starting Dasha balance from canonical Moon position
  const moon = candidateKundali.planets.find(p => p.name === 'Moon');
  const moonLon = moon ? moon.longitude : 0;
  const nakIdx = Math.floor(moonLon / (360 / 27));
  const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
  const firstDasha = candidateKundali.dashas && candidateKundali.dashas.length > 0 ? candidateKundali.dashas[0] : undefined;
  const startingDashaBalance = firstDasha
    ? `${firstDasha.planet} Mahadasha (ends ${firstDasha.endDate.split('T')[0]})`
    : `${nakObj.lord} Mahadasha`;

  return {
    candidateDateTime: targetDateUtc.toISOString(),
    date: targetDateStr,
    timeString: targetTimeStr,
    offsetMinutes,
    offsetSeconds,
    scores: {
      eventFit: 0,
      dashaFit: 0,
      transitFit: 0,
      vargaFit: 0,
      nakshatraFit: 0,
      jaiminiFit: 0,
      ashtakavargaFit: 0,
      lagnaFit: 0,
      kpFit: 0,
      boundaryStability: 80,
      crossModelAgreement: 0,
      contradictionPenalty: 0,
      correlatedPenalty: 0
    },
    evidenceRankingScore: 0,
    totalScore: 0,
    vargaLagnas,
    kpSubLords,
    startingDashaBalance,
    activeMoonNakshatra: nakObj.name,
    activeLagnaDegree: ascDegree,
    supportingEvidence: [],
    contradictions: [],
    kundali: candidateKundali
  };
}

// ============================================================================
// SECTION 13: CANONICAL CANDIDATE GENERATION ACROSS CONTINUOUS ENVELOPE
// ============================================================================

export function generateCanonicalCandidates(
  params: {
    baseDate: string;
    baseTime: string;
    searchWindowMinutes: number;
    stepMinutes?: number;
    coordinates?: { latitude: number; longitude: number };
    timezoneOffsetMinutes?: number;
    customStartMs?: number;
    customEndMs?: number;
  }
): CandidateTime[] {
  const {
    baseDate = '1992-04-23',
    baseTime = '12:00:00',
    searchWindowMinutes = 15,
    stepMinutes = 2,
    coordinates = { latitude: 28.6139, longitude: 77.2090 },
    timezoneOffsetMinutes = 330,
    customStartMs,
    customEndMs
  } = params;

  // Convert base local datetime to UTC
  const [bY = 1992, bM = 4, bD = 23] = baseDate.split('-').map(Number);
  const [bH = 12, bMin = 0, bS = 0] = baseTime.split(':').map(Number);

  const localBaseMs = Date.UTC(bY, bM - 1, bD, bH, bMin, bS);
  const baseUtcMs = localBaseMs - timezoneOffsetMinutes * 60 * 1000;
  const baseDateUtc = new Date(baseUtcMs);

  const windowMs = searchWindowMinutes * 60 * 1000;
  const startMs = customStartMs !== undefined ? customStartMs : (baseUtcMs - windowMs);
  const endMs = customEndMs !== undefined ? customEndMs : (baseUtcMs + windowMs);

  const stepMs = Math.max(15 * 1000, stepMinutes * 60 * 1000);
  const candidates: CandidateTime[] = [];

  for (let tMs = startMs; tMs <= endMs; tMs += stepMs) {
    const targetDateUtc = new Date(tMs);
    const candidate = createCandidateFromTimestamp(
      targetDateUtc,
      baseDateUtc,
      coordinates,
      timezoneOffsetMinutes,
      baseTime
    );
    candidates.push(candidate);
  }

  return candidates;
}

// Legacy adapter for UI backwards compatibility
export function generateTimeCandidates(
  reported: string,
  earliest: string,
  latest: string,
  stepMinutes: number = 2,
  context?: { birthDate?: string; coordinates?: { latitude: number; longitude: number }; timezoneOffsetMinutes?: number }
): CandidateTime[] {
  const repMin = parseHmsToMinutes(reported);
  const startMin = parseHmsToMinutes(earliest);
  const endMin = parseHmsToMinutes(latest);

  const birthDate = context?.birthDate || '1992-04-23';
  const coords = context?.coordinates || { latitude: 28.6139, longitude: 77.2090 };
  const tzOffset = context?.timezoneOffsetMinutes !== undefined ? context.timezoneOffsetMinutes : 330;

  const [bY = 1992, bM = 4, bD = 23] = birthDate.split('-').map(Number);
  const localBaseMs = Date.UTC(bY, bM - 1, bD, Math.floor(repMin / 60), Math.floor(repMin % 60), 0);
  const baseUtcMs = localBaseMs - tzOffset * 60 * 1000;

  const startUtcMs = baseUtcMs + (startMin - repMin) * 60 * 1000;
  const endUtcMs = baseUtcMs + (endMin - repMin) * 60 * 1000;

  return generateCanonicalCandidates({
    baseDate: birthDate,
    baseTime: reported,
    searchWindowMinutes: Math.abs(endMin - startMin) / 2,
    stepMinutes,
    coordinates: coords,
    timezoneOffsetMinutes: tzOffset,
    customStartMs: startUtcMs,
    customEndMs: endUtcMs
  });
}

// ============================================================================
// SECTION 14, 15, 16, 17: REAL PREDICTIVE EVIDENCE EVALUATION
// ============================================================================

export function evaluateEventAlignment(
  cand: CandidateTime,
  ev: RectificationEvent
): {
  eventFit: number;
  dashaFit: number;
  transitFit: number;
  vargaFit: number;
  nakshatraFit: number;
  jaiminiFit: number;
  ashtakavargaFit: number;
  lagnaFit: number;
  kpFit: number;
  supporting: SupportingEvidenceItem[];
  contradictions: ContradictionItem[];
} {
  const supporting: SupportingEvidenceItem[] = [];
  const contradictions: ContradictionItem[] = [];

  const kundali = cand.kundali;
  if (!kundali) {
    return {
      eventFit: 70, dashaFit: 70, transitFit: 70, vargaFit: 70, nakshatraFit: 70,
      jaiminiFit: 70, ashtakavargaFit: 70, lagnaFit: 70, kpFit: 70,
      supporting, contradictions
    };
  }

  const eventDateObj = new Date(ev.date);
  const eventDateMs = eventDateObj.getTime();

  // 1. HISTORICAL DASHA TIMING (Parashari Vimshottari)
  let dashaFit = 60;
  let activeMdLord: PlanetName | undefined;
  let activeAdLord: PlanetName | undefined;

  if (kundali.dashas && kundali.dashas.length > 0) {
    for (const md of kundali.dashas) {
      const mdStartMs = new Date(md.startDate).getTime();
      const mdEndMs = new Date(md.endDate).getTime();

      if (eventDateMs >= mdStartMs && eventDateMs < mdEndMs) {
        activeMdLord = md.planet;
        if (md.antardashas) {
          for (const ad of md.antardashas) {
            const adStartMs = new Date(ad.startDate).getTime();
            const adEndMs = new Date(ad.endDate).getTime();
            if (eventDateMs >= adStartMs && eventDateMs < adEndMs) {
              activeAdLord = ad.planet;
              break;
            }
          }
        }
        break;
      }
    }
  }

  if (activeMdLord && activeAdLord) {
    // Check connections to event houses and significators
    const mdPlanet = kundali.planets.find(p => p.name === activeMdLord);
    const adPlanet = kundali.planets.find(p => p.name === activeAdLord);

    const mdSignifies = Boolean(
      (mdPlanet && ev.astrologicalHouses.includes(mdPlanet.house)) ||
      ev.planets.includes(activeMdLord)
    );
    const adSignifies = Boolean(
      (adPlanet && ev.astrologicalHouses.includes(adPlanet.house)) ||
      ev.planets.includes(activeAdLord)
    );

    const isMdDusthana = mdPlanet && [6, 8, 12].includes(mdPlanet.house) && !ev.astrologicalHouses.includes(mdPlanet.house);
    const isAdDusthana = adPlanet && [6, 8, 12].includes(adPlanet.house) && !ev.astrologicalHouses.includes(adPlanet.house);

    if (mdSignifies && adSignifies) {
      dashaFit = 92;
      supporting.push({
        event: ev.description || ev.category,
        method: 'Parashari',
        explanation: `Mahadasha lord ${activeMdLord} and Antardasha lord ${activeAdLord} simultaneously activate event house(s) and karakas.`,
        strength: 'Strong'
      });
    } else if (mdSignifies || adSignifies) {
      dashaFit = 78;
      supporting.push({
        event: ev.description || ev.category,
        method: 'Parashari',
        explanation: `Dasha lord (${mdSignifies ? activeMdLord : activeAdLord}) directly activates event significance.`,
        strength: 'Moderate'
      });
    } else if (isMdDusthana && isAdDusthana && ev.tier === 'TIER_A') {
      dashaFit = 40;
      contradictions.push({
        event: ev.description || ev.category,
        method: 'Parashari',
        nature: `Event occurred during unsupportive Dusthana Dasha (${activeMdLord}-${activeAdLord}) with no connection to primary event houses.`,
        severity: 'High',
        penalty: 8
      });
    } else {
      dashaFit = 62;
    }
  }

  // 2. HISTORICAL DOUBLE TRANSIT (Guru-Shani Gochar Rule, Devakeralam)
  let transitFit = 60;
  try {
    const doubleTransitHouses = calculateDoubleTransit(kundali.planets, eventDateObj);
    const hasDirectDoubleTransit = ev.astrologicalHouses.some(h => doubleTransitHouses.includes(h));

    if (hasDirectDoubleTransit) {
      transitFit = 94;
      supporting.push({
        event: ev.description || ev.category,
        method: 'Transits',
        explanation: `Simultaneous Double Transit of Jupiter and Saturn aspected/occupied event house (${ev.astrologicalHouses[0]}) on the milestone date.`,
        strength: 'Strong'
      });
    } else {
      // Check trinal/kendra aspect to primary house
      const primaryHouse = ev.astrologicalHouses[0] || 1;
      const kendraTrines = [primaryHouse, ((primaryHouse + 3) % 12) + 1, ((primaryHouse + 4) % 12) + 1, ((primaryHouse + 6) % 12) + 1, ((primaryHouse + 8) % 12) + 1];
      const hasSupportiveTransit = kendraTrines.some(h => doubleTransitHouses.includes(h));

      if (hasSupportiveTransit) {
        transitFit = 80;
      } else {
        transitFit = 55;
      }
    }
  } catch (err) {
    transitFit = 65;
  }

  // 3. VARGA CORROBORATION
  let vargaFit = 70;
  const targetVarga = ev.vargas[0] || 'D9';
  const vargaSign = cand.vargaLagnas[targetVarga];
  if (vargaSign) {
    const signIdx = ZODIAC_SIGNS.indexOf(vargaSign as ZodiacSign);
    const signLord = ZODIAC_LORDS[vargaSign as ZodiacSign];
    const lordPlanet = kundali.planets.find(p => p.name === signLord);

    if (lordPlanet && [1, 4, 5, 7, 9, 10].includes(lordPlanet.house)) {
      vargaFit = 88;
      supporting.push({
        event: ev.description || ev.category,
        method: 'Varga Corroboration',
        explanation: `Favorable ${targetVarga} Lagna in ${vargaSign} with dispositor placed beneficially.`,
        strength: 'Strong'
      });
    } else if (lordPlanet && [6, 8, 12].includes(lordPlanet.house) && ev.tier === 'TIER_A') {
      vargaFit = 48;
      contradictions.push({
        event: ev.description || ev.category,
        method: 'Varga Corroboration',
        nature: `${targetVarga} Lagna lord is uncomfortably placed in Dusthana during this milestone event.`,
        severity: 'Medium',
        penalty: 5
      });
    } else {
      vargaFit = 72;
    }
  }

  // 4. KP CUSP SUB-LORD CORROBORATION
  let kpFit = 68;
  const primaryHouse = ev.astrologicalHouses[0] || 1;
  const subLord = cand.kpSubLords ? cand.kpSubLords[primaryHouse] : undefined;
  if (subLord) {
    const subPlanet = kundali.planets.find(p => p.name === subLord);
    if (subPlanet && [2, 7, 10, 11, 5, 1, 9].includes(subPlanet.house)) {
      kpFit = 88;
      supporting.push({
        event: ev.description || ev.category,
        method: 'KP',
        explanation: `Cusp sub-lord for House ${primaryHouse} (${subLord}) signifies manifestation houses without negative obstruction.`,
        strength: 'Moderate'
      });
    } else if (subPlanet && [6, 8, 12].includes(subPlanet.house) && ev.tier === 'TIER_A') {
      kpFit = 48;
      contradictions.push({
        event: ev.description || ev.category,
        method: 'KP',
        nature: `Cusp sub-lord (${subLord}) occupies detrimental negation house (${subPlanet.house}) for ${ev.category}.`,
        severity: 'Medium',
        penalty: 5
      });
    }
  }

  // 5. ASHTAKAVARGA & LAGNA
  let ashtakavargaFit = 70;
  if (kundali.ashtakvarga) {
    const savArr = kundali.ashtakvarga.byHouse?.sarvashtakvarga || kundali.ashtakvarga.sarvashtakvarga;
    if (savArr) {
      const housePoints = savArr[primaryHouse - 1] || 28;
      ashtakavargaFit = housePoints >= 30 ? 88 : housePoints >= 28 ? 76 : 58;
    }
  }

  const ascPlanet = kundali.planets.find(p => p.name === 'Ascendant');
  const lagnaLord = ascPlanet ? ascPlanet.signLord : 'Mars';
  const lagnaLordPlanet = kundali.planets.find(p => p.name === lagnaLord);
  const lagnaFit = lagnaLordPlanet && [1, 4, 5, 7, 9, 10].includes(lagnaLordPlanet.house) ? 85 : 68;
  const nakshatraFit = 75;
  const jaiminiFit = 72;

  // Aggregate event fit
  const eventFit = Math.round(dashaFit * 0.35 + transitFit * 0.35 + vargaFit * 0.15 + kpFit * 0.15);

  return {
    eventFit,
    dashaFit,
    transitFit,
    vargaFit,
    nakshatraFit,
    jaiminiFit,
    ashtakavargaFit,
    lagnaFit,
    kpFit,
    supporting,
    contradictions
  };
}

// ============================================================================
// SECTION 16, 17, 18 & 19: FORENSIC SCORING & WEIGHTED EVIDENCE ACCUMULATION
// EPISTEMIC CLASSIFICATION: [PRODUCT METHODOLOGY / COMPUTATIONAL HEURISTICS]
// Note: While the qualitative astrological principles (Dasha lordship activation,
// Devakeralam double transit, Varga dignity, KP cusp sub-lords) derive from
// classical authorities, all floating-point numerical weights, multipliers,
// and threshold values below are strictly PRODUCT METHODOLOGY / COMPUTATIONAL
// HEURISTICS designed for deterministic multi-stream ranking and anti-overfitting.
// ============================================================================

export function calculateCandidateScores(
  candidates: CandidateTime[],
  events: RectificationEvent[],
  inputAudit?: InputIntegrityAudit
): CandidateTime[] {
  if (events.length === 0) {
    return candidates.map(c => ({
      ...c,
      evidenceRankingScore: 70,
      totalScore: 70,
      scores: { ...c.scores, eventFit: 70 }
    }));
  }

  // Pre-calculate cluster and independence penalties (Section 9 & 11)
  const { clusters, correlatedPenalty } = detectEventClusters(events);

  return candidates.map(cand => {
    let totalWeight = 0;
    const supportingEvidence: SupportingEvidenceItem[] = [...(cand.supportingEvidence || [])];
    const contradictions: ContradictionItem[] = [...(cand.contradictions || [])];

    let eventFitAccum = 0;
    let dashaFitAccum = 0;
    let transitFitAccum = 0;
    let vargaFitAccum = 0;
    let nakshatraFitAccum = 0;
    let jaiminiFitAccum = 0;
    let ashtakavargaFitAccum = 0;
    let lagnaFitAccum = 0;
    let kpFitAccum = 0;

    events.forEach(ev => {
      // 1. Quality Tier weight (Section 6)
      let tierWeight = 1.0;
      if (ev.tier === 'TIER_A') tierWeight = 3.5;
      else if (ev.tier === 'TIER_B') tierWeight = 1.8;
      else if (ev.tier === 'TIER_C') tierWeight = 0.4; // Tier C can never independently decide BTR

      // 2. Date Precision weight (Section 7)
      let precisionWeight = 1.0;
      if (ev.datePrecision === 'Exact') precisionWeight = 1.4;
      else if (ev.datePrecision === 'High') precisionWeight = 1.2;
      else if (ev.datePrecision === 'Moderate') precisionWeight = 0.9;
      else if (ev.datePrecision === 'Low') precisionWeight = 0.6;
      else precisionWeight = 0.3;

      // 3. Source Reliability weight (Section 8)
      let sourceWeight = 1.0;
      if (ev.sourceReliability === 'Official document' || ev.sourceReliability === 'Hospital record' || ev.sourceReliability === 'Marriage certificate') {
        sourceWeight = 1.3;
      } else if (ev.sourceReliability === 'Contemporaneous diary' || ev.sourceReliability === 'Employment record') {
        sourceWeight = 1.1;
      } else if (ev.sourceReliability === 'Family memory' || ev.sourceReliability === 'Personal recollection') {
        sourceWeight = 0.9;
      } else {
        sourceWeight = 0.7;
      }

      // 4. Cluster redundancy discount: non-primary cluster events receive 0.3 weight
      const isSubsequentInCluster = clusters.some(cl => cl.eventIds.includes(ev.id) && cl.primaryEventId !== ev.id);
      const clusterMultiplier = isSubsequentInCluster ? 0.3 : 1.0;

      const eventWeight = tierWeight * precisionWeight * sourceWeight * clusterMultiplier;
      totalWeight += eventWeight;

      // Real predictive evaluation
      const evalResult = evaluateEventAlignment(cand, ev);

      eventFitAccum += evalResult.eventFit * eventWeight;
      dashaFitAccum += evalResult.dashaFit * eventWeight;
      transitFitAccum += evalResult.transitFit * eventWeight;
      vargaFitAccum += evalResult.vargaFit * eventWeight;
      nakshatraFitAccum += evalResult.nakshatraFit * eventWeight;
      jaiminiFitAccum += evalResult.jaiminiFit * eventWeight;
      ashtakavargaFitAccum += evalResult.ashtakavargaFit * eventWeight;
      lagnaFitAccum += evalResult.lagnaFit * eventWeight;
      kpFitAccum += evalResult.kpFit * eventWeight;

      supportingEvidence.push(...evalResult.supporting);
      contradictions.push(...evalResult.contradictions);
    });

    const divisor = totalWeight || 1;
    const scores: CandidateScores = {
      eventFit: Math.round(eventFitAccum / divisor),
      dashaFit: Math.round(dashaFitAccum / divisor),
      transitFit: Math.round(transitFitAccum / divisor),
      vargaFit: Math.round(vargaFitAccum / divisor),
      nakshatraFit: Math.round(nakshatraFitAccum / divisor),
      jaiminiFit: Math.round(jaiminiFitAccum / divisor),
      ashtakavargaFit: Math.round(ashtakavargaFitAccum / divisor),
      lagnaFit: Math.round(lagnaFitAccum / divisor),
      kpFit: Math.round(kpFitAccum / divisor),
      boundaryStability: cand.scores.boundaryStability,
      crossModelAgreement: 0,
      contradictionPenalty: 0,
      correlatedPenalty
    };

    // Calculate cross-model standard deviation
    const subscores = [
      scores.eventFit,
      scores.dashaFit,
      scores.transitFit,
      scores.vargaFit,
      scores.kpFit,
      scores.jaiminiFit,
      scores.ashtakavargaFit
    ];
    const avg = subscores.reduce((a, b) => a + b, 0) / subscores.length;
    const variance = subscores.reduce((sum, s) => sum + Math.pow(s - avg, 2), 0) / subscores.length;
    const sd = Math.sqrt(variance);
    scores.crossModelAgreement = Math.round(Math.max(15, 100 - (sd * 2.8)));

    // Total Contradiction Penalty (Section 18)
    const totalContradictionPenalty = contradictions.reduce((sum, c) => sum + c.penalty, 0);
    scores.contradictionPenalty = Math.min(30, totalContradictionPenalty);

    // Section 19: Evidence Ranking Score computation
    const rawWeighted =
      scores.eventFit * 0.20 +
      scores.dashaFit * 0.16 +
      scores.kpFit * 0.14 +
      scores.transitFit * 0.12 +
      scores.vargaFit * 0.10 +
      scores.lagnaFit * 0.08 +
      scores.jaiminiFit * 0.08 +
      scores.ashtakavargaFit * 0.06 +
      scores.crossModelAgreement * 0.06;

    // Apply penalties
    const penalizedScore = rawWeighted - scores.contradictionPenalty - (scores.correlatedPenalty * 0.3);
    const finalEvidenceScore = Math.max(10, Math.min(99, Math.round(penalizedScore)));

    return {
      ...cand,
      scores,
      evidenceRankingScore: finalEvidenceScore,
      totalScore: finalEvidenceScore,
      supportingEvidence: supportingEvidence.slice(0, 5),
      contradictions: contradictions.slice(0, 4)
    };
  }).sort((a, b) => b.evidenceRankingScore - a.evidenceRankingScore);
}

// ============================================================================
// SECTION 14 & 20: JUSTIFIED PRECISION & PLATEAU / INTERVAL DETERMINATION
// ============================================================================

export function determineJustifiedInterval(
  candidates: CandidateTime[],
  confidence: 'VERY HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE'
): {
  intervalString: string;
  precisionString: string;
  isPlateau: boolean;
  plateauCandidates: CandidateTime[];
} {
  if (candidates.length === 0) {
    return {
      intervalString: 'Unknown',
      precisionString: '±15 minutes',
      isPlateau: false,
      plateauCandidates: []
    };
  }

  const top = candidates[0];
  const topScore = top.evidenceRankingScore;

  // [PRODUCT METHODOLOGY - EMPIRICAL HEURISTIC]:
  // A 2.5-point score proximity threshold represents measurement tolerance
  // within civil and family reporting variance.
  // RA-01 / F-L9-BTR-12: Contiguous Plateau Formation without Valley Bridging
  const sorted = [...candidates].sort((a, b) => parseHmsToMinutes(a.timeString) - parseHmsToMinutes(b.timeString));
  const topIdx = sorted.findIndex(c => c.timeString === top.timeString);

  const contiguousPlateau: CandidateTime[] = [top];

  // Backward contiguous expansion from peak
  if (topIdx >= 0) {
    for (let i = topIdx - 1; i >= 0; i--) {
      const c = sorted[i];
      const scoreDiff = topScore - c.evidenceRankingScore;
      const sameVarga = c.vargaLagnas['D1'] === top.vargaLagnas['D1'] && c.vargaLagnas['D9'] === top.vargaLagnas['D9'];
      if (scoreDiff <= 2.5 && sameVarga) {
        contiguousPlateau.unshift(c);
      } else {
        break; // Valley or boundary encountered: terminate backward expansion
      }
    }

    // Forward contiguous expansion from peak
    for (let i = topIdx + 1; i < sorted.length; i++) {
      const c = sorted[i];
      const scoreDiff = topScore - c.evidenceRankingScore;
      const sameVarga = c.vargaLagnas['D1'] === top.vargaLagnas['D1'] && c.vargaLagnas['D9'] === top.vargaLagnas['D9'];
      if (scoreDiff <= 2.5 && sameVarga) {
        contiguousPlateau.push(c);
      } else {
        break; // Valley or boundary encountered: terminate forward expansion
      }
    }
  }

  if (contiguousPlateau.length > 1) {
    const minTimeCandidate = contiguousPlateau[0];
    const maxTimeCandidate = contiguousPlateau[contiguousPlateau.length - 1];

    const spanMinutes = Math.abs(Math.round(parseHmsToMinutes(maxTimeCandidate.timeString) - parseHmsToMinutes(minTimeCandidate.timeString)));
    const halfSpan = (Math.max(1, spanMinutes) / 2).toFixed(1);

    return {
      intervalString: `${minTimeCandidate.timeString.slice(0, 5)} – ${maxTimeCandidate.timeString.slice(0, 5)}`,
      precisionString: `±${halfSpan} minutes (Interval: ${minTimeCandidate.timeString.slice(0, 5)} – ${maxTimeCandidate.timeString.slice(0, 5)})`,
      isPlateau: true,
      plateauCandidates: contiguousPlateau
    };
  }

  // Single candidate dominates: justified precision based on boundary stability
  let justifiedPrec = '±1.5 minutes';
  if (confidence === 'VERY HIGH') justifiedPrec = '±45 seconds';
  else if (confidence === 'HIGH') justifiedPrec = '±1.5 minutes';
  else if (confidence === 'MODERATE') justifiedPrec = '±3 minutes';
  else justifiedPrec = '±15 minutes';

  return {
    intervalString: top.timeString.slice(0, 5),
    precisionString: justifiedPrec,
    isPlateau: false,
    plateauCandidates: [top]
  };
}

// ============================================================================
// SECTION 21: LEAVE-ONE-OUT (LOO) VALIDATION
// ============================================================================

export function performLOOValidation(
  candidates: CandidateTime[],
  events: RectificationEvent[]
): LOOResult[] {
  if (events.length < 3) return [];

  const originalScores = calculateCandidateScores(candidates, events);
  const originalBest = originalScores[0];

  const validationResults: LOOResult[] = [];

  events.forEach(ev => {
    const partialEvents = events.filter(e => e.id !== ev.id);
    const partialScores = calculateCandidateScores(candidates, partialEvents);
    const partialBest = partialScores[0];

    const origMin = parseHmsToMinutes(originalBest.timeString);
    const partMin = parseHmsToMinutes(partialBest.timeString);
    const timeDiff = Math.abs(origMin - partMin);

    validationResults.push({
      removedEventId: ev.id,
      removedEventDescription: ev.description || ev.category,
      originalBestTime: originalBest.timeString,
      newBestTime: partialBest.timeString,
      fitPreserved: timeDiff <= 2.0, // Stable within 2 minutes
      scoreDifference: originalBest.evidenceRankingScore - partialBest.evidenceRankingScore
    });
  });

  return validationResults;
}

// ============================================================================
// SECTION 22: HOLD-OUT VALIDATION
// ============================================================================

export function performHoldOutValidation(
  candidates: CandidateTime[],
  events: RectificationEvent[]
): HoldOutResult | null {
  if (events.length < 4) return null;

  const half = Math.ceil(events.length / 2);
  const training = events.slice(0, half);
  const validation = events.slice(half);

  const trainScores = calculateCandidateScores(candidates, training);
  const bestTrain = trainScores[0];

  const valScores = calculateCandidateScores(candidates, validation);
  const valCandOnTrainTime = valScores.find(c => c.timeString === bestTrain.timeString);

  const valScore = valCandOnTrainTime ? valCandOnTrainTime.evidenceRankingScore : 0;

  return {
    trainingEventsCount: training.length,
    validationEventsCount: validation.length,
    bestTrainingTime: bestTrain.timeString,
    trainingScore: bestTrain.evidenceRankingScore,
    validationScoreOnTrainingTime: valScore,
    predictionSuccess: valScore >= 65
  };
}

// ============================================================================
// SECTION 23: ALTERNATIVE CANDIDATES EVALUATION
// ============================================================================

export function evaluateAlternativeCandidates(
  scoredCandidates: CandidateTime[]
): AlternativeCandidate[] {
  if (scoredCandidates.length <= 1) return [];

  const top = scoredCandidates[0];
  const alternatives: AlternativeCandidate[] = [];

  // Filter distinct candidates that are at least 2.5 minutes apart from top candidate
  const distinct = scoredCandidates.filter(c =>
    Math.abs(parseHmsToMinutes(c.timeString) - parseHmsToMinutes(top.timeString)) >= 2.0
  );

  distinct.slice(0, 3).forEach(cand => {
    const margin = top.evidenceRankingScore - cand.evidenceRankingScore;
    let rejectionReason = `Scores ${margin} points lower overall.`;

    if (cand.contradictions.length > top.contradictions.length) {
      rejectionReason += ` Exhibits ${cand.contradictions.length} astrological contradictions (${cand.contradictions[0]?.nature || ''}).`;
    } else if (cand.scores.dashaFit < top.scores.dashaFit) {
      rejectionReason += ` Weaker Dasha alignment (${cand.scores.dashaFit} vs ${top.scores.dashaFit}).`;
    } else if (cand.scores.kpFit < top.scores.kpFit) {
      rejectionReason += ` Weaker KP cusp sub-lord corroboration.`;
    }

    alternatives.push({
      timeString: cand.timeString,
      evidenceScore: cand.evidenceRankingScore,
      interval: `${cand.timeString.slice(0, 5)} (Δ ${cand.offsetMinutes >= 0 ? '+' : ''}${cand.offsetMinutes}m)`,
      rejectionReason,
      margin
    });
  });

  return alternatives;
}

// ============================================================================
// SECTION 32 & 38: RUN FORENSIC BTR ENGINE (COARSE-TO-FINE MULTI-PASS PIPELINE)
// ============================================================================

export function runBtrEngine(params: BtrEngineRunParams): ForensicBtrResult {
  const {
    baseTime,
    birthDate = '1992-04-23',
    placeName = 'Coordinates Station',
    coordinates = { latitude: 28.6139, longitude: 77.2090 },
    timezoneOffsetMinutes = 330,
    timeSource = 'Family recollection',
    responses = {},
    events: explicitEvents,
    searchWindowMinutes: customWindowMinutes,
    workingTime: initialWorkingTime,
    confirmedTime = null,
    confirmationStatus = 'UNCONFIRMED'
  } = params;

  const cascadeStages: CascadeStageSummary[] = [];

  // 1. Input Integrity Audit (Section 3 & 4)
  const inputAudit = performInputIntegrityAudit(
    baseTime,
    birthDate,
    placeName,
    coordinates.latitude,
    coordinates.longitude,
    timezoneOffsetMinutes,
    timeSource,
    customWindowMinutes
  );

  // 2. Build RectificationEvent objects
  let events: RectificationEvent[] = explicitEvents || [];
  if (events.length === 0) {
    const responseEntries = Object.entries(responses).filter(([_, val]) =>
      Boolean(val && (val.date || val.value || typeof val === 'string'))
    );

    events = responseEntries.map(([qId, val], i) => {
      const matchingQ =
        STRUCTURAL_QUESTIONS.find(q => q.id === qId) ||
        STRUCTURAL_QUESTIONS[i % STRUCTURAL_QUESTIONS.length];

      const dateStr = typeof val === 'object' && val.date ? val.date : (typeof val === 'string' ? val : '2015-06-15');
      const rawPrecision = typeof val === 'object' && val.precision ? val.precision : 'Exact';

      return {
        id: `resp-evt-${qId}`,
        category: matchingQ.category,
        tier: matchingQ.tier,
        description: (typeof val === 'object' && val.description) ? val.description : `Event for ${matchingQ.category}`,
        date: dateStr,
        datePrecision: rawPrecision,
        sourceReliability: (typeof val === 'object' && val.sourceReliability) ? val.sourceReliability : 'Personal recollection',
        location: (typeof val === 'object' && val.location) ? val.location : undefined,
        importance: matchingQ.tier === 'TIER_A' ? 'Very High' : matchingQ.tier === 'TIER_B' ? 'High' : 'Medium',
        reliability: rawPrecision === 'Exact' ? 'High' : 'Medium',
        astrologicalHouses: matchingQ.houses,
        planets: matchingQ.planets,
        vargas: matchingQ.vargas
      };
    });
  }

  const { clusters, independentEventCount } = detectEventClusters(events);

  // 3. Setup Canonical Timestamp Envelope
  const searchSpan = inputAudit.searchWindowMinutes;
  const [bY = 1992, bM = 4, bD = 23] = birthDate.split('-').map(Number);
  const [bH = 12, bMin = 0, bS = 0] = baseTime.split(':').map(Number);
  const localBaseMs = Date.UTC(bY, bM - 1, bD, bH, bMin, bS);
  const baseUtcMs = localBaseMs - timezoneOffsetMinutes * 60 * 1000;

  const startUtcMs = baseUtcMs - searchSpan * 60 * 1000;
  const endUtcMs = baseUtcMs + searchSpan * 60 * 1000;

  const searchEnvelope: SearchEnvelope = {
    startDateTime: new Date(startUtcMs).toISOString(),
    endDateTime: new Date(endUtcMs).toISOString(),
    windowMinutes: searchSpan,
    source: timeSource,
    rationale: `Enveloping ${inputAudit.searchWindowEnvelope} around recorded baseline ${birthDate} ${baseTime}`
  };

  // PASS 1: COARSE SEARCH
  let coarseStep = 2;
  if (searchSpan >= 525600) coarseStep = 10080; // 7 days step for multi-year
  else if (searchSpan >= 43200) coarseStep = 1440; // 1 day step for multi-month
  else if (searchSpan >= 1440) coarseStep = 120; // 2 hour step for multi-day
  else if (searchSpan >= 360) coarseStep = 15;
  else if (searchSpan >= 120) coarseStep = 6;
  else if (searchSpan >= 60) coarseStep = 3;
  else coarseStep = 1.5;

  const coarseCandidates = generateCanonicalCandidates({
    baseDate: birthDate,
    baseTime,
    searchWindowMinutes: searchSpan,
    stepMinutes: coarseStep,
    coordinates,
    timezoneOffsetMinutes,
    customStartMs: startUtcMs,
    customEndMs: endUtcMs
  });

  const coarseScored = calculateCandidateScores(coarseCandidates, events, inputAudit);
  const bestCoarse = coarseScored[0] || coarseCandidates[0];

  cascadeStages.push({
    stage: 'Macro/Sign',
    searchSpan: `±${searchSpan}m (${coarseCandidates[0]?.timeString.slice(0, 5)} - ${coarseCandidates[coarseCandidates.length - 1]?.timeString.slice(0, 5)})`,
    stepSize: `${coarseStep} min`,
    evaluatedCandidates: coarseCandidates.length,
    bestTime: bestCoarse.timeString,
    bestScore: bestCoarse.evidenceRankingScore
  });

  // Check for Multi-Year Ambiguity / Disparate Peaks (Section 24)
  const isMultiYearOrWide = searchSpan >= 1440;
  const highScoringCoarse = coarseScored.filter(c => (bestCoarse.evidenceRankingScore - c.evidenceRankingScore) <= 3.0);
  const hasDisparatePeaks = isMultiYearOrWide && highScoringCoarse.length > 3 &&
    Math.abs(parseHmsToMinutes(highScoringCoarse[0].timeString) - parseHmsToMinutes(highScoringCoarse[highScoringCoarse.length - 1].timeString)) > 120;

  // PASS 2: MESO SEARCH (Zoom around best coarse candidate)
  const [cYear, cMonth, cDay] = bestCoarse.date.split('-').map(Number);
  const [cH, cM, cS] = bestCoarse.timeString.split(':').map(Number);
  const mesoCenterLocalMs = Date.UTC(cYear, cMonth - 1, cDay, cH, cM, cS);
  const mesoCenterUtcMs = mesoCenterLocalMs - timezoneOffsetMinutes * 60 * 1000;

  const mesoSpanMinutes = Math.min(searchSpan, Math.max(4, coarseStep * 2));
  const mesoStepMinutes = 0.5; // 30 seconds

  const mesoCandidates = generateCanonicalCandidates({
    baseDate: bestCoarse.date,
    baseTime: bestCoarse.timeString,
    searchWindowMinutes: mesoSpanMinutes,
    stepMinutes: mesoStepMinutes,
    coordinates,
    timezoneOffsetMinutes,
    customStartMs: mesoCenterUtcMs - mesoSpanMinutes * 60 * 1000,
    customEndMs: mesoCenterUtcMs + mesoSpanMinutes * 60 * 1000
  });

  const mesoScored = calculateCandidateScores(mesoCandidates, events, inputAudit);
  const bestMeso = mesoScored[0] || bestCoarse;

  cascadeStages.push({
    stage: 'Meso/Minutes',
    searchSpan: `±${mesoSpanMinutes.toFixed(1)}m around ${bestCoarse.timeString.slice(0, 5)}`,
    stepSize: '30 sec',
    evaluatedCandidates: mesoCandidates.length,
    bestTime: bestMeso.timeString,
    bestScore: bestMeso.evidenceRankingScore
  });

  // PASS 3: MICRO SEARCH (Fine seconds adjustment)
  const [mYear, mMonth, mDay] = bestMeso.date.split('-').map(Number);
  const [mH, mM, mS] = bestMeso.timeString.split(':').map(Number);
  const microCenterLocalMs = Date.UTC(mYear, mMonth - 1, mDay, mH, mM, mS);
  const microCenterUtcMs = microCenterLocalMs - timezoneOffsetMinutes * 60 * 1000;

  const microSpanMinutes = 1.5; // ±90 seconds
  const microStepMinutes = 0.25; // 15 seconds

  const microCandidates = generateCanonicalCandidates({
    baseDate: bestMeso.date,
    baseTime: bestMeso.timeString,
    searchWindowMinutes: microSpanMinutes,
    stepMinutes: microStepMinutes,
    coordinates,
    timezoneOffsetMinutes,
    customStartMs: microCenterUtcMs - microSpanMinutes * 60 * 1000,
    customEndMs: microCenterUtcMs + microSpanMinutes * 60 * 1000
  });

  const microScored = calculateCandidateScores(microCandidates, events, inputAudit);
  const bestMicro = microScored[0] || bestMeso;

  cascadeStages.push({
    stage: 'Micro/Seconds',
    searchSpan: `±90s around ${bestMeso.timeString}`,
    stepSize: '15 sec',
    evaluatedCandidates: microCandidates.length,
    bestTime: bestMicro.timeString,
    bestScore: bestMicro.evidenceRankingScore
  });

  // SECTION 24 & 25: CONFIDENCE CLASSIFICATION
  const tierACount = events.filter(e => e.tier === 'TIER_A').length;
  const tierBCount = events.filter(e => e.tier === 'TIER_B').length;

  let confidence: 'VERY HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE' = 'MODERATE';

  if (events.length === 0 || (tierACount === 0 && tierBCount === 0) || hasDisparatePeaks) {
    confidence = 'INCONCLUSIVE';
  } else if (tierACount >= 3 && bestMicro.evidenceRankingScore >= 80 && bestMicro.contradictions.length === 0) {
    confidence = 'VERY HIGH';
  } else if (tierACount >= 2 && bestMicro.evidenceRankingScore >= 74 && bestMicro.contradictions.length <= 1) {
    confidence = 'HIGH';
  } else if (events.length >= 2 && bestMicro.evidenceRankingScore >= 64) {
    confidence = 'MODERATE';
  } else {
    confidence = 'LOW';
  }

  // SECTION 14: JUSTIFIED INTERVAL FORMATION
  const { intervalString, precisionString, isPlateau } = determineJustifiedInterval(
    mesoScored,
    confidence
  );

  // Anti-Overfitting Validations (Section 21 & 22)
  const looResults = performLOOValidation(mesoCandidates, events);
  const holdOutResult = performHoldOutValidation(mesoCandidates, events);

  const stableLooCount = looResults.filter(r => r.fitPreserved).length;
  const looStabilityText = looResults.length > 0
    ? `${stableLooCount} of ${looResults.length} event ablations preserved optimal window (${Math.round((stableLooCount / looResults.length) * 100)}% stability)`
    : 'Insufficient events (<3) for ablation testing';

  const overfittingRisk: 'Low' | 'Moderate' | 'High' =
    looResults.length > 0 && stableLooCount / looResults.length >= 0.75
      ? 'Low'
      : looResults.length > 0 && stableLooCount / looResults.length >= 0.5
      ? 'Moderate'
      : 'High';

  // Section 23: Alternative Candidates
  const alternativeCandidates = evaluateAlternativeCandidates(mesoScored);

  // Delta seconds relative to recorded base time
  const offsetSeconds = bestMicro.offsetSeconds;

  // Section 11 & 12: Methodology Agreement Matrix
  const methodologyAgreement: MethodologyAgreementItem[] = [
    {
      methodology: 'Parashari D1 Bhavas & Lords',
      score: bestMicro.scores.lagnaFit,
      status: bestMicro.scores.lagnaFit >= 75 ? 'Strongly Supports' : 'Supports',
      details: `Lagna placed at ${bestMicro.vargaLagnas['D1']} (${bestMicro.activeLagnaDegree?.toFixed(2)}°) with supportive lord alignments.`
    },
    {
      methodology: 'Vimshottari Dasha Timing',
      score: bestMicro.scores.dashaFit,
      status: bestMicro.scores.dashaFit >= 78 ? 'Strongly Supports' : bestMicro.scores.dashaFit >= 65 ? 'Supports' : 'Contradicts',
      details: `${bestMicro.startingDashaBalance}. Planetary dasha lords correlate with reported event anchors.`
    },
    {
      methodology: 'KP System (Cusp Sub-Lords)',
      score: bestMicro.scores.kpFit,
      status: bestMicro.scores.kpFit >= 75 ? 'Strongly Supports' : 'Supports',
      details: `Cusp sub-lords for primary bhavas confirm event fruition without detrimental negation.`
    },
    {
      methodology: 'Varga Corroboration (D9, D10, D7)',
      score: bestMicro.scores.vargaFit,
      status: bestMicro.scores.vargaFit >= 75 ? 'Strongly Supports' : 'Partial',
      details: `Navamsha (D9) Lagna in ${bestMicro.vargaLagnas['D9']}, Dashamsha (D10) Lagna in ${bestMicro.vargaLagnas['D10']}.`
    },
    {
      methodology: 'Double Transit of Jupiter & Saturn',
      score: bestMicro.scores.transitFit,
      status: bestMicro.scores.transitFit >= 75 ? 'Strongly Supports' : 'Supports',
      details: `Correlates simultaneous transits of Shani and Guru to primary event houses during reported event dates.`
    },
    {
      methodology: 'Jaimini Chara Dasha & Karakas',
      score: bestMicro.scores.jaiminiFit,
      status: bestMicro.scores.jaiminiFit >= 70 ? 'Supports' : 'Partial',
      details: `Amatyakaraka (AmK) and Darakaraka (DK) alignments corroborate life milestone fruitions.`
    }
  ];

  // SECTION 33: Language Safety & Epistemic Honesty
  let finalConclusion = '';
  if (confidence === 'INCONCLUSIVE') {
    finalConclusion = hasDisparatePeaks
      ? "Wide candidate search produced multiple disparate high-scoring regions across the timeline. A unique rectification cannot be responsibly asserted. Acharya review with additional documentary Tier-A life events is required."
      : "The available evidence is insufficient or contradictory. A unique rectification cannot be responsibly asserted. Further documentary Tier-A life events are required before adopting a modified birth time.";
  } else if (isPlateau) {
    finalConclusion = `The available evidence strongly favours the birth-time interval ${intervalString} (justified precision ${precisionString}). Within this window, key divisional charts and dasha periods remain uniform; higher mathematical precision to the exact second is not justified by current factual evidence.`;
  } else {
    finalConclusion = `The available evidence is most consistent with a birth time of ${bestMicro.timeString} (interval: ${intervalString}, justified precision ${precisionString}). This candidate demonstrates the highest cross-model convergence across Parashari, KP, and Varga methodologies with minimal contradictory indications.`;
  }

  const remainingUncertainty = isPlateau
    ? `An uncertainty of approximately ${precisionString} remains due to uniform astrological dignity across adjacent minutes.`
    : `A residual uncertainty of ${precisionString} remains attributable to human memory tolerance and civil record variance.`;

  return {
    timestamp: bestMicro.timeString,
    offsetSeconds,
    confidence,
    confidenceScore: bestMicro.evidenceRankingScore,
    precisionJustified: precisionString,
    mostSupportedInterval: intervalString,
    recordedTime: baseTime,
    workingTime: initialWorkingTime || bestMicro.timeString,
    confirmedTime,
    confirmationStatus,
    bestCandidate: bestMicro,
    strongestSupportingEvidence: bestMicro.supportingEvidence,
    strongestContradictoryEvidence: bestMicro.contradictions,
    methodologyAgreement,
    alternativeCandidates,
    validationResult: {
      looStability: looStabilityText,
      holdOutAccuracy: holdOutResult ? `${holdOutResult.validationScoreOnTrainingTime}% accuracy` : undefined,
      overfittingRisk,
      summary: `Leave-One-Out cross-validation indicates ${overfittingRisk.toLowerCase()} sensitivity to individual event outliers.`
    },
    remainingUncertainty,
    finalConclusion,
    inputAudit,
    cascadeStages,
    searchEnvelope,
    auditTrail: {
      engineVersion: BTR_ENGINE_VERSION,
      ruleset: BTR_RULESET_VERSION,
      evaluatedCandidatesCount: coarseCandidates.length + mesoCandidates.length + microCandidates.length,
      eventsProcessed: events.length,
      independentEventCount,
      timestamp: new Date().toISOString()
    }
  };
}
